import type {
  DocumentProcessingStatus,
  DocumentRecordTarget,
  DocumentReviewReason
} from '@klear/schema/document-processing'

const REVIEW_REASON_LABELS: Record<DocumentReviewReason, string> = {
  too_large: 'The file is too large to process',
  unsupported_format: 'The format cannot be processed',
  no_text_layer: 'The pages carry no text layer',
  trader_unresolved: 'The trader could not be resolved',
  model_failed: 'The run did not finish',
  preview_failed: 'The pages could not be rendered',
  low_confidence: 'What the run extracted is uncertain'
}

const REVIEW_REASON_LOOKUP: Partial<Record<string, string>> = REVIEW_REASON_LABELS

export function reviewReasonLabel(reason: string): string {
  return REVIEW_REASON_LOOKUP[reason] ?? reason
}

export const NEXT_STEPS = [
  'readyToApprove',
  'waitingOnYou',
  'waitingOnParent',
  'blocked',
  'processing',
  'creatingRecords',
  'notProcessed',
  'filed',
  'filedAutomatically',
  'dismissed',
  'filedByLaterRun',
  'superseded'
] as const

export type NextStep = (typeof NEXT_STEPS)[number]

export type ReviewFacts = {
  status: DocumentProcessingStatus
  reviewReason: DocumentReviewReason | null
  run: number
  filingRun: number | null
  filedAutomatically: boolean
  proposalCount: number
  filingCount: number
  openQuestionCount: number
  notImportWork: boolean
  pendingParent: boolean
}

type StepRule = NextStep | 'fromDenial' | 'fromPlan' | 'fromFiling'

const STEP_BY_STATUS: Record<DocumentProcessingStatus, StepRule> = {
  processing: 'processing',
  needs_review: 'fromPlan',
  failed: 'fromPlan',
  approved: 'creatingRecords',
  executing: 'creatingRecords',
  executed: 'fromFiling',
  denied: 'fromDenial',
  superseded: 'superseded'
}

const BLOCKS_THE_PLAN: Record<DocumentReviewReason, boolean> = {
  too_large: true,
  unsupported_format: true,
  no_text_layer: true,
  preview_failed: true,
  trader_unresolved: true,
  model_failed: true,
  low_confidence: false
}

export function isFilingDocument(record: DocumentRecordTarget): boolean {
  return record?.kind === 'attach_payment' || record?.fileDocument !== false
}

export function childOrderEventIdsOf(record: DocumentRecordTarget): number[] {
  if (record == null || record.kind === 'attach_payment') return []
  return record.childOrderEventIds ?? []
}

export function parentIsResolved(record: DocumentRecordTarget): boolean {
  if (record == null) return true
  if (record.kind !== 'create') return true
  return record.parentOrderEventId != null || record.parentProposalOrdinal != null
}

export function blocksThePlan(reason: DocumentReviewReason | null): boolean {
  return reason !== null && BLOCKS_THE_PLAN[reason]
}

export function reviewStep(facts: ReviewFacts): NextStep {
  const rule = STEP_BY_STATUS[facts.status]
  if (rule === 'fromDenial') return deniedStep(facts)
  if (rule === 'fromFiling') return facts.filedAutomatically ? 'filedAutomatically' : 'filed'
  if (rule !== 'fromPlan') return rule
  if (isBlocked(facts)) return 'blocked'
  if (facts.openQuestionCount > 0) return 'waitingOnYou'
  return facts.pendingParent ? 'waitingOnParent' : 'readyToApprove'
}

function deniedStep(facts: ReviewFacts): NextStep {
  if (facts.filingRun !== null && facts.filingRun > facts.run) return 'filedByLaterRun'
  return 'dismissed'
}

function isBlocked(facts: ReviewFacts): boolean {
  if (facts.notImportWork) return true
  if (blocksThePlan(facts.reviewReason)) return true
  if (facts.proposalCount === 0) return true
  return facts.filingCount === 0
}
